import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getApiUrl } from 'data/services/apiConstants';
import { SIGN_IN_SUCCESS } from 'reducers/userReducer';
import { navigateAfterAuth } from './resumeCheckoutAfterAuth';

/**
 * Social OAuth Callback Handler
 *
 * Handles redirect from backend after successful OAuth login
 * Extracts JWT token from URL and stores in Redux + localStorage
 */
const SocialCallback = () => {
	const API_URL = getApiUrl();
	const location = useLocation();
	const history = useHistory();
	const dispatch = useDispatch();
	const [error, setError] = useState('');

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const token = searchParams.get('token');
		const errorParam = searchParams.get('error');

		if (errorParam) {
			setError(decodeURIComponent(errorParam));

			// Redirect to signin after 3 seconds
			setTimeout(() => {
				history.push('/signin?error=' + encodeURIComponent(errorParam));
			}, 3000);
			return;
		}

		if (token) {
			// Store token in localStorage
			localStorage.setItem('token', token);

			// Fetch user data with token
			fetchUserData(token);
		} else {
			setError('No authentication token received');
			setTimeout(() => {
				history.push('/signin?error=Authentication failed');
			}, 3000);
		}
		// Process the callback once per location; fetchUserData is recreated each
		// render, so listing it would repeat the token exchange.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, history, dispatch]);

	/**
	 * Fetch authenticated user data from backend
	 *
	 * @param {string} token - JWT token
	 */
	const fetchUserData = async (token) => {
		try {
			const response = await fetch(`${API_URL}/api/auth/user`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			const data = await response.json();

			if (data.status === 'success' && data.data) {
				dispatch({
					type: SIGN_IN_SUCCESS,
					payload: {
						userData: data.data,
						authToken: token,
						isSigningIn: false,
						signInErrors: [],
					},
				});

				localStorage.removeItem('redirectAfterLogin');
				await navigateAfterAuth({
					dispatch,
					history,
					urlParams: new URLSearchParams(),
				});
			} else {
				throw new Error('Failed to fetch user data');
			}
		} catch (err) {
			console.error('Error fetching user data:', err);
			setError('Failed to complete authentication');

			setTimeout(() => {
				history.push('/signin?error=Failed to complete login');
			}, 3000);
		}
	};

	return (
		<div className="callback-page">
			<div className="callback-container">
				{error ? (
					<>
						<div className="callback-error-icon">⚠️</div>
						<h2>Authentication Failed</h2>
						<p className="callback-error">{error}</p>
						<p className="callback-redirect">Redirecting to sign in...</p>
					</>
				) : (
					<>
						<div className="callback-spinner"></div>
						<h2>Completing Sign In</h2>
						<p>Please wait while we authenticate your account...</p>
					</>
				)}
			</div>

			<style jsx>{`
				.callback-page {
					display: flex;
					align-items: center;
					justify-content: center;
					min-height: 100vh;
					background: #f5f5f5;
				}

				.callback-container {
					background: white;
					padding: 48px 32px;
					border-radius: 8px;
					box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
					text-align: center;
					max-width: 400px;
					width: 100%;
				}

				.callback-spinner {
					width: 48px;
					height: 48px;
					border: 4px solid #f3f3f3;
					border-top: 4px solid #1976d2;
					border-radius: 50%;
					margin: 0 auto 24px;
					animation: spin 1s linear infinite;
				}

				.callback-error-icon {
					font-size: 48px;
					margin-bottom: 16px;
				}

				.callback-container h2 {
					margin: 0 0 12px 0;
					color: #333;
					font-size: 24px;
				}

				.callback-container p {
					margin: 8px 0;
					color: #666;
					font-size: 14px;
				}

				.callback-error {
					color: #d32f2f;
					font-weight: 500;
				}

				.callback-redirect {
					margin-top: 16px;
					font-size: 13px;
					color: #999;
				}

				@keyframes spin {
					0% {
						transform: rotate(0deg);
					}
					100% {
						transform: rotate(360deg);
					}
				}
			`}</style>
		</div>
	);
};

export default SocialCallback;
